import React, { useState, useEffect, useMemo } from 'react';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Nav from 'react-bootstrap/Nav';
import Button from 'react-bootstrap/Button';
import Badge from 'react-bootstrap/Badge';
import Table from 'react-bootstrap/Table';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import Alert from 'react-bootstrap/Alert';
import Form from 'react-bootstrap/Form';
import {
  getDashboardStats,
  getSubcontractors,
  updateSubcontractor,
  getCompanies,
  updateCompany,
  getMessages,
  updateMessage,
  adminLogout,
} from '../../api/admin';

function AdminDashboard({ user, onLogout }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [subcontractors, setSubcontractors] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Search state
  const [subcontractorSearch, setSubcontractorSearch] = useState('');
  const [companySearch, setCompanySearch] = useState('');

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsData, subData, compData, msgData] = await Promise.all([
        getDashboardStats(),
        getSubcontractors(),
        getCompanies(),
        getMessages(),
      ]);
      setStats(statsData);
      setSubcontractors(subData);
      setCompanies(compData);
      setMessages(msgData);
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await adminLogout();
      onLogout();
    } catch (err) {
      console.error('Logout error:', err);
      onLogout();
    }
  };

  const handleStatusChange = async (type, id, newStatus) => {
    try {
      if (type === 'subcontractor') {
        await updateSubcontractor(id, { status: newStatus });
        setSubcontractors(subcontractors.map(s =>
          s.id === id ? { ...s, status: newStatus } : s
        ));
      } else if (type === 'company') {
        await updateCompany(id, { status: newStatus });
        setCompanies(companies.map(c =>
          c.id === id ? { ...c, status: newStatus } : c
        ));
      }
      loadData(); // Refresh stats
    } catch (err) {
      setError('Failed to update status');
    }
  };

  const handleMessageRead = async (id, isRead) => {
    try {
      await updateMessage(id, { is_read: isRead });
      setMessages(messages.map(m =>
        m.id === id ? { ...m, is_read: isRead } : m
      ));
      loadData(); // Refresh stats
    } catch (err) {
      setError('Failed to update message');
    }
  };

  const openDetailModal = (type, item) => {
    setModalType(type);
    setSelectedItem(item);
    setShowModal(true);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const EQUIPMENT_NAMES = {
    chainsaw: 'Chainsaw',
    wood_chipper: 'Wood Chipper',
    stump_grinder: 'Stump Grinder',
    bucket_truck: 'Bucket Truck',
    crane: 'Crane',
    skid_steer: 'Skid Steer',
    dump_truck: 'Dump Truck',
    grapple_truck: 'Grapple Truck',
    trailer: 'Trailer',
    front_loader: 'Front Loader',
    excavator: 'Excavator',
    bulldozer: 'Bulldozer',
  };

  const filteredSubcontractors = useMemo(() => {
    if (!subcontractorSearch.trim()) return subcontractors;
    const q = subcontractorSearch.toLowerCase();
    return subcontractors.filter((s) => {
      const fieldMatch = [s.first_name, s.last_name, s.email, s.company_name, s.city, s.state, s.status]
        .filter(Boolean)
        .some((field) => field.toLowerCase().includes(q));
      if (fieldMatch) return true;
      if (s.equipment_experience) {
        return Object.entries(s.equipment_experience).some(([id, val]) =>
          val.hasExperience && (EQUIPMENT_NAMES[id] || id).toLowerCase().includes(q)
        );
      }
      return false;
    });
  }, [subcontractors, subcontractorSearch]);

  const SERVICE_NAMES = {
    tree_removal: 'Tree Removal',
    tree_trimming: 'Tree Trimming & Pruning',
    stump_grinding: 'Stump Grinding',
    emergency_services: 'Emergency Tree Services',
    lot_clearing: 'Lot Clearing',
    brush_clearing: 'Brush Clearing',
    debris_hauling: 'Debris Hauling',
    wood_chipping: 'Wood Chipping',
    right_of_way: 'Right-of-Way Clearing',
    vegetation_management: 'Vegetation Management',
  };

  const filteredCompanies = useMemo(() => {
    if (!companySearch.trim()) return companies;
    const q = companySearch.toLowerCase();
    return companies.filter((c) => {
      const fieldMatch = [c.company_name, c.contact_first_name, c.contact_last_name, c.contact_email, c.company_type, c.city, c.state, c.status]
        .filter(Boolean)
        .some((field) => field.toLowerCase().includes(q));
      if (fieldMatch) return true;
      if (c.services_provided) {
        return Object.entries(c.services_provided).some(([id, val]) =>
          val.needed !== false && (SERVICE_NAMES[id] || id).toLowerCase().includes(q)
        );
      }
      return false;
    });
  }, [companies, companySearch]);

  const getStatusBadge = (status) => {
    const variants = {
      pending: 'warning',
      approved: 'success',
      rejected: 'danger',
    };
    return <Badge bg={variants[status] || 'secondary'}>{status}</Badge>;
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" variant="success" />
        <p className="mt-3">Loading dashboard...</p>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      {/* Header */}
      <Row className="mb-4 align-items-center">
        <Col>
          <h2 className="mb-0">Admin Dashboard</h2>
          <small className="text-muted">Welcome, {user.username}</small>
        </Col>
        <Col xs="auto">
          <Button variant="outline-secondary" size="sm" onClick={handleLogout}>
            Logout
          </Button>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Navigation Tabs */}
      <Nav variant="tabs" className="mb-4" activeKey={activeTab} onSelect={setActiveTab}>
        <Nav.Item>
          <Nav.Link eventKey="overview">Overview</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="subcontractors">
            Subcontractors
            {stats?.subcontractors.pending > 0 && (
              <Badge bg="warning" className="ms-2">{stats.subcontractors.pending}</Badge>
            )}
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="companies">
            Companies
            {stats?.companies.pending > 0 && (
              <Badge bg="warning" className="ms-2">{stats.companies.pending}</Badge>
            )}
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="messages">
            Messages
            {stats?.messages.unread > 0 && (
              <Badge bg="danger" className="ms-2">{stats.messages.unread}</Badge>
            )}
          </Nav.Link>
        </Nav.Item>
      </Nav>

      {/* Overview Tab */}
      {activeTab === 'overview' && stats && (
        <Row className="g-4">
          <Col xs={12} md={4}>
            <Card className="h-100 border-primary">
              <Card.Header className="bg-primary text-white">Subcontractors</Card.Header>
              <Card.Body>
                <h3>{stats.subcontractors.total}</h3>
                <div className="d-flex gap-3 text-muted">
                  <span><Badge bg="warning">{stats.subcontractors.pending}</Badge> Pending</span>
                  <span><Badge bg="success">{stats.subcontractors.approved}</Badge> Approved</span>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12} md={4}>
            <Card className="h-100 border-success">
              <Card.Header className="bg-success text-white">Companies</Card.Header>
              <Card.Body>
                <h3>{stats.companies.total}</h3>
                <div className="d-flex gap-3 text-muted">
                  <span><Badge bg="warning">{stats.companies.pending}</Badge> Pending</span>
                  <span><Badge bg="success">{stats.companies.approved}</Badge> Approved</span>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12} md={4}>
            <Card className="h-100 border-info">
              <Card.Header className="bg-info text-white">Messages</Card.Header>
              <Card.Body>
                <h3>{stats.messages.total}</h3>
                <div className="text-muted">
                  <span><Badge bg="danger">{stats.messages.unread}</Badge> Unread</span>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Subcontractors Tab */}
      {activeTab === 'subcontractors' && (
        <Card>
          <Card.Body>
            <Form.Control
              type="search"
              placeholder="Search by name, email, company, city, state, status, or equipment..."
              value={subcontractorSearch}
              onChange={(e) => setSubcontractorSearch(e.target.value)}
              className="mb-3"
            />
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Company</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredSubcontractors.map((sub) => (
                  <tr key={sub.id}>
                    <td>{sub.first_name} {sub.last_name}</td>
                    <td>{sub.email}</td>
                    <td>{sub.company_name || '-'}</td>
                    <td>{getStatusBadge(sub.status)}</td>
                    <td>{formatDate(sub.created_at)}</td>
                    <td>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="me-1"
                        onClick={() => openDetailModal('subcontractor', sub)}
                      >
                        View
                      </Button>
                      {sub.status === 'pending' && (
                        <>
                          <Button
                            variant="success"
                            size="sm"
                            className="me-1"
                            onClick={() => handleStatusChange('subcontractor', sub.id, 'approved')}
                          >
                            Approve
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleStatusChange('subcontractor', sub.id, 'rejected')}
                          >
                            Reject
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
                {filteredSubcontractors.length === 0 && (
                  <tr>
                    <td colSpan="6" className="text-center text-muted py-4">
                      {subcontractorSearch ? 'No matching subcontractors found' : 'No subcontractor registrations yet'}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      {/* Companies Tab */}
      {activeTab === 'companies' && (
        <Card>
          <Card.Body>
            <Form.Control
              type="search"
              placeholder="Search by company, contact, email, type, city, state, status, or service..."
              value={companySearch}
              onChange={(e) => setCompanySearch(e.target.value)}
              className="mb-3"
            />
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Contact</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCompanies.map((company) => (
                  <tr key={company.id}>
                    <td>{company.company_name}</td>
                    <td>{company.contact_first_name} {company.contact_last_name}</td>
                    <td>{company.company_type}</td>
                    <td>{getStatusBadge(company.status)}</td>
                    <td>{formatDate(company.created_at)}</td>
                    <td>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="me-1"
                        onClick={() => openDetailModal('company', company)}
                      >
                        View
                      </Button>
                      {company.status === 'pending' && (
                        <>
                          <Button
                            variant="success"
                            size="sm"
                            className="me-1"
                            onClick={() => handleStatusChange('company', company.id, 'approved')}
                          >
                            Approve
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleStatusChange('company', company.id, 'rejected')}
                          >
                            Reject
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
                {filteredCompanies.length === 0 && (
                  <tr>
                    <td colSpan="6" className="text-center text-muted py-4">
                      {companySearch ? 'No matching companies found' : 'No company registrations yet'}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      {/* Messages Tab */}
      {activeTab === 'messages' && (
        <Card>
          <Card.Body>
            <Table responsive hover>
              <thead>
                <tr>
                  <th>From</th>
                  <th>Subject</th>
                  <th>Type</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {messages.map((msg) => (
                  <tr key={msg.id} className={!msg.is_read ? 'table-warning' : ''}>
                    <td>
                      {msg.first_name} {msg.last_name}
                      <br />
                      <small className="text-muted">{msg.email}</small>
                    </td>
                    <td>{msg.subject}</td>
                    <td>{msg.inquiry_type}</td>
                    <td>{formatDate(msg.created_at)}</td>
                    <td>
                      {msg.is_read ? (
                        <Badge bg="secondary">Read</Badge>
                      ) : (
                        <Badge bg="danger">Unread</Badge>
                      )}
                    </td>
                    <td>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="me-1"
                        onClick={() => {
                          openDetailModal('message', msg);
                          if (!msg.is_read) {
                            handleMessageRead(msg.id, true);
                          }
                        }}
                      >
                        View
                      </Button>
                      {!msg.is_read && (
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => handleMessageRead(msg.id, true)}
                        >
                          Mark Read
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
                {messages.length === 0 && (
                  <tr>
                    <td colSpan="6" className="text-center text-muted py-4">
                      No messages yet
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      {/* Detail Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {modalType === 'subcontractor' && 'Subcontractor Details'}
            {modalType === 'company' && 'Company Details'}
            {modalType === 'message' && 'Message Details'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedItem && modalType === 'subcontractor' && (
            <div>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Name:</strong> {selectedItem.first_name} {selectedItem.last_name}
                </Col>
                <Col md={6}>
                  <strong>Company:</strong> {selectedItem.company_name || 'N/A'}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Email:</strong> {selectedItem.email}
                </Col>
                <Col md={6}>
                  <strong>Phone:</strong> {selectedItem.phone}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col>
                  <strong>Address:</strong> {selectedItem.address}, {selectedItem.city}, {selectedItem.state} {selectedItem.zip_code}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Years in Business:</strong> {selectedItem.years_in_business}
                </Col>
                <Col md={6}>
                  <strong>Has Insurance:</strong> {selectedItem.has_insurance ? 'Yes' : 'No'}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col>
                  <strong>Work Locations:</strong>
                  <div className="mt-1">
                    {selectedItem.work_locations?.map((state) => (
                      <Badge key={state} bg="primary" className="me-1">{state}</Badge>
                    ))}
                  </div>
                </Col>
              </Row>
              {selectedItem.additional_notes && (
                <Row className="mb-3">
                  <Col>
                    <strong>Notes:</strong>
                    <p className="mb-0">{selectedItem.additional_notes}</p>
                  </Col>
                </Row>
              )}
            </div>
          )}

          {selectedItem && modalType === 'company' && (
            <div>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Company:</strong> {selectedItem.company_name}
                </Col>
                <Col md={6}>
                  <strong>Type:</strong> {selectedItem.company_type}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Contact:</strong> {selectedItem.contact_first_name} {selectedItem.contact_last_name}
                </Col>
                <Col md={6}>
                  <strong>Title:</strong> {selectedItem.contact_title || 'N/A'}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Email:</strong> {selectedItem.contact_email}
                </Col>
                <Col md={6}>
                  <strong>Phone:</strong> {selectedItem.contact_phone}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col>
                  <strong>Address:</strong> {selectedItem.address}, {selectedItem.city}, {selectedItem.state} {selectedItem.zip_code}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col>
                  <strong>Service Areas:</strong>
                  <div className="mt-1">
                    {selectedItem.service_areas?.map((state) => (
                      <Badge key={state} bg="success" className="me-1">{state}</Badge>
                    ))}
                  </div>
                </Col>
              </Row>
            </div>
          )}

          {selectedItem && modalType === 'message' && (
            <div>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>From:</strong> {selectedItem.first_name} {selectedItem.last_name}
                </Col>
                <Col md={6}>
                  <strong>Company:</strong> {selectedItem.company_name || 'N/A'}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Email:</strong> {selectedItem.email}
                </Col>
                <Col md={6}>
                  <strong>Phone:</strong> {selectedItem.phone || 'N/A'}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Type:</strong> {selectedItem.inquiry_type}
                </Col>
                <Col md={6}>
                  <strong>Date:</strong> {formatDate(selectedItem.created_at)}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col>
                  <strong>Subject:</strong> {selectedItem.subject}
                </Col>
              </Row>
              <Row>
                <Col>
                  <strong>Message:</strong>
                  <Card className="mt-2 bg-light">
                    <Card.Body>
                      <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>{selectedItem.message}</p>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default AdminDashboard;
